/**
 * PDF viewer component using react-pdf.
 *
 * Displays a PDF with page navigation controls.
 */

import { useState, useEffect, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from "lucide-react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  /** PDF file as a Blob or URL string */
  file: Blob | string | null;
  /** Target page to display (1-indexed) */
  targetPage?: number;
  /** Text to highlight on the current page */
  highlightText?: string;
  /** Increment to force re-triggering highlight (for same page/text clicks) */
  highlightKey?: number;
  /** Callback when page changes */
  onPageChange?: (page: number) => void;
  /** Width of the viewer */
  width?: number;
  /** Optional className */
  className?: string;
}

export function PDFViewer({
  file,
  targetPage,
  highlightText,
  highlightKey,
  onPageChange,
  width = 600,
  className,
}: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  // Reference to the page container for DOM-based highlighting
  const pageContainerRef = useRef<HTMLDivElement>(null);

  // Flash overlay state for image-only PDFs
  const [showFlash, setShowFlash] = useState(false);

  // Apply highlighting when highlightText, pageNumber, or highlightKey changes
  useEffect(() => {
    if (!highlightText) {
      // Clear highlights if no text
      const node = pageContainerRef.current;
      if (node) {
        const spans = node.querySelectorAll('.react-pdf__Page__textContent span');
        spans.forEach((span) => {
          (span as HTMLElement).style.backgroundColor = '';
          (span as HTMLElement).style.color = '';
          (span as HTMLElement).style.borderRadius = '';
        });
      }
      return;
    }

    let attempts = 0;
    let intervalId: NodeJS.Timeout | null = null;

    const applyHighlighting = (): boolean => {
      const node = pageContainerRef.current;
      if (!node) return false;

      const textLayer = node.querySelector('.react-pdf__Page__textContent');
      if (!textLayer) return false;

      const spans = textLayer.querySelectorAll('span');

      // Image-only PDF: no text spans to highlight.
      // Show a brief flash on the page to indicate provenance navigation.
      if (spans.length === 0) {
        setShowFlash(true);
        setTimeout(() => setShowFlash(false), 1200);
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return true;
      }

      // Clear all previous highlights
      spans.forEach((span) => {
        (span as HTMLElement).style.backgroundColor = '';
        (span as HTMLElement).style.color = '';
        (span as HTMLElement).style.borderRadius = '';
      });

      const normalizedHighlight = highlightText.toLowerCase().trim();

      const dollarAmounts = highlightText.match(/\$[\d,]+\.?\d*/g) || [];
      const numbers = highlightText.match(/[\d,]{4,}\.?\d*/g) || []; // 4+ digit numbers
      const datePatterns = highlightText.match(/(?:january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{1,2},?\s+\d{4}/gi) || [];

      const significantWords = normalizedHighlight
        .split(/\s+/)
        .filter(w => {
          const originalWord = highlightText.split(/\s+/).find(ow => ow.toLowerCase() === w);
          const isAllCaps = originalWord && originalWord === originalWord.toUpperCase() && w.length >= 2;
          const isLong = w.length >= 8;
          return isAllCaps || isLong;
        });

      const allWords = normalizedHighlight
        .split(/[\s,.()-]+/)
        .filter(w => w.length >= 3 && !/^\d+$/.test(w));

      let matchCount = 0;
      let firstMatch: HTMLElement | null = null;

      spans.forEach((span) => {
        const text = span.textContent || '';
        const lowerText = text.toLowerCase().trim();
        if (lowerText.length === 0) return;

        let isMatch = false;

        for (const amount of dollarAmounts) {
          const numPart = amount.replace('$', '').replace(/,/g, '');
          if (text.includes(amount) || text.includes(numPart)) {
            isMatch = true;
            break;
          }
        }

        if (!isMatch) {
          for (const date of datePatterns) {
            if (lowerText.includes(date.toLowerCase())) {
              isMatch = true;
              break;
            }
          }
        }

        if (!isMatch) {
          for (const num of numbers) {
            const cleanNum = num.replace(/,/g, '');
            if (text.includes(num) || text.includes(cleanNum)) {
              isMatch = true;
              break;
            }
          }
        }

        if (!isMatch) {
          for (const word of significantWords) {
            if (lowerText.includes(word)) {
              isMatch = true;
              break;
            }
          }
        }

        if (!isMatch && normalizedHighlight.length <= 50) {
          if (lowerText.includes(normalizedHighlight) || normalizedHighlight.includes(lowerText)) {
            isMatch = true;
          }
        }

        if (!isMatch && allWords.length > 0) {
          for (const word of allWords) {
            if (lowerText.includes(word)) {
              isMatch = true;
              break;
            }
          }
        }

        if (isMatch) {
          (span as HTMLElement).style.backgroundColor = '#FFFF00';
          (span as HTMLElement).style.color = '#000000';
          (span as HTMLElement).style.borderRadius = '2px';
          matchCount++;
          if (!firstMatch) {
            firstMatch = span as HTMLElement;
          }
        }
      });

      if (firstMatch) {
        firstMatch.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return matchCount > 0;
    };

    const tryApplyHighlighting = () => {
      attempts++;
      const found = applyHighlighting();
      if (found || attempts >= 15) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    };

    const timeoutId = setTimeout(() => {
      tryApplyHighlighting();
      if (attempts < 15) {
        intervalId = setInterval(tryApplyHighlighting, 300);
      }
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [highlightText, pageNumber, highlightKey]);

  // Convert Blob to URL when file changes
  useEffect(() => {
    if (file instanceof Blob) {
      const url = URL.createObjectURL(file);
      setFileUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setFileUrl(file);
    }
  }, [file]);

  // Navigate to target page when it changes (clamp to valid range)
  useEffect(() => {
    if (targetPage && targetPage >= 1 && numPages > 0) {
      setPageNumber(Math.min(targetPage, numPages));
    }
  }, [targetPage, numPages]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
    // Navigate to target page if specified
    if (targetPage && targetPage >= 1 && targetPage <= numPages) {
      setPageNumber(targetPage);
    }
  }

  function onDocumentLoadError(err: Error) {
    setLoading(false);
    setError(`Failed to load PDF: ${err.message}`);
  }

  function goToPrevPage() {
    if (pageNumber > 1) {
      const newPage = pageNumber - 1;
      setPageNumber(newPage);
      onPageChange?.(newPage);
    }
  }

  function goToNextPage() {
    if (pageNumber < numPages) {
      const newPage = pageNumber + 1;
      setPageNumber(newPage);
      onPageChange?.(newPage);
    }
  }

  function zoomIn() {
    setScale((prev) => Math.min(prev + 0.2, 2.5));
  }

  function zoomOut() {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  }

  if (!fileUrl) {
    return (
      <div className={`flex items-center justify-center h-full ${className}`}>
        <div className="text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
          <p>Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center h-full ${className}`}>
        <div className="text-center text-destructive">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Controls */}
      <div className="flex items-center justify-between p-2 border-b bg-muted/50">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm min-w-[80px] text-center">
            Page {pageNumber} of {numPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={zoomOut}
            disabled={scale <= 0.5}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm min-w-[50px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="outline"
            size="icon"
            onClick={zoomIn}
            disabled={scale >= 2.5}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* PDF Document */}
      <div className="flex-1 overflow-auto bg-muted/30 flex justify-center">
        <Document
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex items-center justify-center p-8">
              <Skeleton className="h-[800px]" style={{ width }} />
            </div>
          }
        >
          <div ref={pageContainerRef} style={{ position: 'relative' }}>
            <Page
              pageNumber={pageNumber}
              width={width * scale}
              loading={
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              }
            />
            {showFlash && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  border: '3px solid #FFFF00',
                  borderRadius: '4px',
                  pointerEvents: 'none',
                  animation: 'provenance-flash 1.2s ease-out forwards',
                }}
              />
            )}
            <style>{`
              @keyframes provenance-flash {
                0% { opacity: 1; box-shadow: inset 0 0 30px rgba(255,255,0,0.3); }
                70% { opacity: 1; box-shadow: inset 0 0 15px rgba(255,255,0,0.1); }
                100% { opacity: 0; box-shadow: none; }
              }
            `}</style>
          </div>
        </Document>
      </div>
    </div>
  );
}

export default PDFViewer;
